import { publishFormat } from 'Workspace/Publications/utils/constants';
import { subscribeToRTM,fetchFromRTM } from './index';
import * as constants from './constants';
import './typedef';

class ExportResolver {
  constructor(connection) {
    this.connection = connection;
  }

  /**
   * Subscribes to an export. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-export-subscribe/)
   * @param {Object} payload
   * @param {string} payload.exportId
   * @returns {Promise.<{exportId: string}>}
   */
  authorExportSubscribe = async (exportId, listener) => {
    const message = {
      type: constants.RTM_AUTHOR_EXPORT_SUBSCRIBE,
      exportId,
    };

    const subscription = await subscribeToRTM(
      this.connection,
      message,
      listener,
    );

    // `await subscribeToRTM(..)` returns null if the subscription cannot be created.
    // In this case return an unsubscribe function that does nothing.
    if (!subscription) {
      const unsubscribeFn = () => { };
      return unsubscribeFn;
    }

    const unsubscribeMessage = {
      type: constants.RTM_AUTHOR_EXPORT_UNSUBSCRIBE,
      exportId,
    };
    const unsubscribeFn = () => subscription.unsubscribe(unsubscribeMessage);
    return unsubscribeFn;
  };

  /**
   * Initiate an export preflight report. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-activity-export-request)
   * @param {Object} payload
   * @param {string} payload.elementId
   * @param {string} payload.outputType
   * @param {string} payload.elementType
   * @returns {Promise.<exportId: string, exportElementsCount: number>}
   */
  exportForReport = async (elementId, outputType, elementType = 'ACTIVITY') => {
    const message = {
      type: constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST,
      elementId,
      elementType,
      exportType: publishFormat.CHANNEL_PARTNER === outputType ? constants.RTM_AUTHOR_ELEMENT_EXPORT_TYPE_CP_REPORT : constants.RTM_AUTHOR_ELEMENT_EXPORT_TYPE_REPORT,
    };
    if (outputType) message.metadata = `{"outputType": "${outputType}"}`;

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_OK,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_ERROR
    );
    return response;
  }

  /**
   * Get pre-signed URL. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/pre-signed-url-request)
   * @param {exportId} exportId
   * @returns {Promise.<type: string, response: object>}
   */
  getPreSignedURL = async (exportId) => {
    const message = {
      type: constants.RTM_PRE_SIGNED_URL_REQUEST,
      exportId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PRE_SIGNED_URL_REQUEST_OK,
      constants.RTM_PRE_SIGNED_URL_REQUEST_ERROR
    );
    return response;
  }

  /**
   * Initiates an element export for a preview. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-element-export-request/)
   * @param {Object} payload
   * @param {string} payload.elementId
   * @param {string} payload.elementType
   * @returns {Promise.<exportId: string, exportElementsCount: number>}
   */
  exportForPreview = async (elementId, elementType ='ACTIVITY', threeLevelToc, exportType = constants.RTM_AUTHOR_ELEMENT_EXPORT_TYPE_PREVIEW, market) => {
    const message = {
      type: constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST,
      elementId,
      elementType,
      metadata: threeLevelToc ? "{\"tocLevel\":\"Three Levels\"}" : "{}",
      exportType,
      ...(market && { market: market }),
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_OK,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_ERROR
    );

    return response;
  };
}

export default ExportResolver;
